/**
 * Levels & XP Service
 * Manages user levels, XP points, and the global levels configuration.
 *
 * IMPORTANT: Orders integration will call awardPointsForOrder() later.
 * This module currently only handles config and computation - no actual point awarding yet.
 */
import { doc, onSnapshot, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { getFirebaseDb } from '../firebase/firebase.js'
import logger from './logger.js'

const log = logger.createScoped('LevelsService')

// Collection/document paths
const CONFIG_COLLECTION = 'Config'
const LEVELS_DOC_ID = 'Levels'
const USERS_COLLECTION = 'Users'

/**
 * Default levels configuration - seeded if Config/Levels doc is missing
 */
const DEFAULT_LEVELS_CONFIG = {
  conversion: {
    amount_ils: 1000,
    points: 1,
  },
  levels: [
    { level: 1, name: 'מתחיל', minPoints: 0, badge: 'starter', colorKey: 'gray' },
    { level: 2, name: 'מתקדם', minPoints: 5, badge: 'intermediate', colorKey: 'blue' },
    { level: 3, name: 'מקצוען', minPoints: 15, badge: 'pro', colorKey: 'purple' },
    { level: 4, name: 'אלוף', minPoints: 30, badge: 'champion', colorKey: 'gold' },
  ],
  updatedAt: null,
  updatedBy: 'system',
}

/**
 * Subscribe to Config/Levels document with real-time updates.
 * If the document doesn't exist, it will be seeded with defaults.
 *
 * @param {Function} callback - Receives (config, error) where config is the levels config object
 * @returns {Function} Unsubscribe function
 */
export function subscribeLevelsConfig(callback) {
  log.debug('Subscribing to levels config')

  const db = getFirebaseDb()
  const docRef = doc(db, CONFIG_COLLECTION, LEVELS_DOC_ID)

  const unsubscribe = onSnapshot(
    docRef,
    async (snapshot) => {
      if (snapshot.exists()) {
        const data = { id: snapshot.id, ...snapshot.data() }
        log.debug('Levels config loaded', { levelsCount: data.levels?.length })
        callback(data, null)
      } else {
        // Document doesn't exist - seed it with defaults
        log.info('Levels config not found, seeding defaults')
        try {
          await seedLevelsConfig()
          // The onSnapshot will fire again with the new data
        } catch (err) {
          log.error('Failed to seed levels config', err)
          callback(null, err)
        }
      }
    },
    (error) => {
      log.error('Levels config subscription error', error)
      callback(null, error)
    }
  )

  return unsubscribe
}

/**
 * Seed the Config/Levels document with default values.
 * Called automatically if the document is missing.
 *
 * @returns {Promise<void>}
 */
export async function seedLevelsConfig() {
  const db = getFirebaseDb()
  const docRef = doc(db, CONFIG_COLLECTION, LEVELS_DOC_ID)

  const seedData = {
    ...DEFAULT_LEVELS_CONFIG,
    updatedAt: serverTimestamp(),
    updatedBy: 'system',
  }

  await setDoc(docRef, seedData)
  log.info('Levels config seeded with defaults')
}

/**
 * Save the entire levels configuration (conversion + levels array).
 *
 * @param {Object} config - The config object to save
 * @param {Object} config.conversion - Conversion rule { amount_ils, points }
 * @param {Array} config.levels - Array of level objects
 * @param {string} updatedBy - UID or email of the user making the update
 * @returns {Promise<void>}
 */
export async function saveLevelsConfig(config, updatedBy) {
  log.info('Saving levels config', { updatedBy })

  const db = getFirebaseDb()
  const docRef = doc(db, CONFIG_COLLECTION, LEVELS_DOC_ID)

  // Sort levels by minPoints before saving
  const sortedLevels = [...config.levels].sort((a, b) => a.minPoints - b.minPoints)

  await setDoc(docRef, {
    conversion: config.conversion,
    levels: sortedLevels,
    updatedAt: serverTimestamp(),
    updatedBy: updatedBy || 'unknown',
  })

  log.info('Levels config saved successfully')
}

/**
 * Compute the user's current level information based on their points.
 *
 * @param {number} points - User's current XP points
 * @param {Object} levelsConfig - The levels config object (from subscribeLevelsConfig)
 * @returns {Object} Level computation result
 *   - level: current level number
 *   - currentLevelObj: full level object for current level
 *   - nextLevelObj: full level object for next level (or null if max)
 *   - progressRatio: 0-1 progress towards next level
 *   - pointsToNext: points needed to reach next level (0 if max)
 *   - isMaxLevel: true if user is at maximum level
 */
/**
 * Calculate points to award based on order total and conversion config.
 * Uses floor rounding (e.g., 2500₪ with 1000:1 ratio = 2 points)
 *
 * @param {number} totalAmount - Order total in ILS (typically totalWithVat)
 * @param {Object} conversion - Conversion config { amount_ils, points }
 * @returns {number} Points to award (floored)
 */
export function calculatePointsFromTotal(totalAmount, conversion) {
  if (!totalAmount || totalAmount <= 0) return 0
  if (!conversion || !conversion.amount_ils || conversion.amount_ils <= 0) return 0

  const points = Math.floor(totalAmount / conversion.amount_ils) * (conversion.points || 1)
  return Math.max(0, points)
}

export function computeLevelFromPoints(points, levelsConfig) {
  if (!levelsConfig || !levelsConfig.levels || levelsConfig.levels.length === 0) {
    // Fallback to basic level 1
    return {
      level: 1,
      currentLevelObj: { level: 1, name: 'מתחיל', minPoints: 0 },
      nextLevelObj: null,
      progressRatio: 0,
      pointsToNext: 0,
      isMaxLevel: true,
    }
  }

  // Sort levels by minPoints ascending
  const sortedLevels = [...levelsConfig.levels].sort((a, b) => a.minPoints - b.minPoints)

  // Find the highest level the user qualifies for
  let currentLevelObj = sortedLevels[0]
  let currentIndex = 0

  for (let i = 0; i < sortedLevels.length; i++) {
    if (points >= sortedLevels[i].minPoints) {
      currentLevelObj = sortedLevels[i]
      currentIndex = i
    } else {
      break
    }
  }

  // Determine next level
  const isMaxLevel = currentIndex >= sortedLevels.length - 1
  const nextLevelObj = isMaxLevel ? null : sortedLevels[currentIndex + 1]

  // Calculate progress ratio
  let progressRatio = 0
  let pointsToNext = 0

  if (!isMaxLevel && nextLevelObj) {
    const pointsInCurrentLevel = points - currentLevelObj.minPoints
    const pointsNeededForNext = nextLevelObj.minPoints - currentLevelObj.minPoints
    progressRatio = pointsNeededForNext > 0 ? pointsInCurrentLevel / pointsNeededForNext : 0
    progressRatio = Math.min(Math.max(progressRatio, 0), 1) // Clamp to 0-1
    pointsToNext = nextLevelObj.minPoints - points
  }

  return {
    level: currentLevelObj.level,
    currentLevelObj,
    nextLevelObj,
    progressRatio,
    pointsToNext,
    isMaxLevel,
  }
}

/**
 * Ensure user has XP fields initialized in their profile.
 * If xp_points or xp_level are missing, set defaults.
 *
 * @param {string} uid - User ID
 * @returns {Promise<void>}
 */
export async function ensureUserXpFields(uid) {
  if (!uid) {
    log.warn('ensureUserXpFields called without uid')
    return
  }

  log.debug('Ensuring user XP fields', { uid })

  const db = getFirebaseDb()
  const userRef = doc(db, USERS_COLLECTION, uid)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    log.warn('User doc does not exist, cannot ensure XP fields', { uid })
    return
  }

  const userData = userSnap.data()
  const updates = {}

  if (typeof userData.xp_points !== 'number') {
    updates.xp_points = 0
  }

  if (typeof userData.xp_level !== 'number') {
    updates.xp_level = 1
  }

  if (Object.keys(updates).length > 0) {
    log.info('Initializing missing XP fields for user', { uid, updates })
    await setDoc(userRef, updates, { merge: true })
  }
}

/**
 * PLACEHOLDER: Award XP points for a completed order.
 *
 * ============================================================
 * ORDERS INTEGRATION WILL CALL THIS FUNCTION LATER
 * ============================================================
 *
 * This function is a stub for future implementation.
 * When the orders system is ready, it should:
 *
 * 1. Be called from order status change handlers (e.g., when order is delivered/paid)
 * 2. Calculate points based on order total and conversion config
 * 3. Update user's xp_points and xp_level
 * 4. Create an XP ledger entry to prevent double-awarding
 *
 * @param {Object} params - Award parameters
 * @param {string} params.uid - User ID to award points to
 * @param {string} params.orderId - Order ID (for idempotency)
 * @param {number} params.orderTotalIls - Order total in ILS
 * @param {Object} params.conversion - Conversion config { amount_ils, points }
 * @param {string} [params.source='order'] - Source of the award
 * @returns {Promise<Object>} Result with pointsAwarded, newTotal, newLevel
 *
 * @example
 * // Future usage:
 * await awardPointsForOrder({
 *   uid: 'user123',
 *   orderId: 'order456',
 *   orderTotalIls: 2500,
 *   conversion: { amount_ils: 1000, points: 1 }
 * })
 * // Would award 2 points (2500 / 1000 = 2.5, floored to 2)
 */
export async function awardPointsForOrder(params) {
  // ============================================================
  // TODO: IMPLEMENT WHEN ORDERS SYSTEM IS READY
  // ============================================================
  //
  // Implementation steps:
  // 1. Check if award already exists in Users/{uid}/XpLedger/{orderId}
  // 2. If exists, return early (idempotent)
  // 3. Calculate points: Math.floor(orderTotalIls / conversion.amount_ils) * conversion.points
  // 4. Get current user xp_points
  // 5. Calculate new total and new level
  // 6. Update user doc with new xp_points and xp_level
  // 7. Create ledger entry with award details
  // 8. Return result
  //
  // Required order fields:
  // - orderId: unique identifier
  // - userId/uid: user to award
  // - totals.gross or totals.net: order value in ILS
  // - status: to determine if order is complete
  // - createdAt, paidAt, deliveredAt: timestamps
  // ============================================================

  log.warn('awardPointsForOrder called but not yet implemented', params)

  throw new Error('awardPointsForOrder is a placeholder - orders integration not yet implemented')
}

/**
 * PLACEHOLDER: Manually adjust user XP (for admin/testing purposes).
 *
 * @param {string} uid - User ID
 * @param {number} pointsDelta - Points to add (positive) or subtract (negative)
 * @param {Object} debugInfo - Debug info { source, notes }
 * @returns {Promise<Object>} Result with newPoints, newLevel
 */
export async function adjustUserXp(uid, pointsDelta, debugInfo = {}) {
  log.info('Adjusting user XP', { uid, pointsDelta, debugInfo })

  const db = getFirebaseDb()
  const userRef = doc(db, USERS_COLLECTION, uid)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    throw new Error('User not found')
  }

  const userData = userSnap.data()
  const currentPoints = userData.xp_points || 0
  const newPoints = Math.max(0, currentPoints + pointsDelta) // Don't go negative

  // Get config to compute new level
  const configRef = doc(db, CONFIG_COLLECTION, LEVELS_DOC_ID)
  const configSnap = await getDoc(configRef)
  const config = configSnap.exists() ? configSnap.data() : DEFAULT_LEVELS_CONFIG

  const levelInfo = computeLevelFromPoints(newPoints, config)

  await setDoc(
    userRef,
    {
      xp_points: newPoints,
      xp_level: levelInfo.level,
      xp_last_calculated_at: serverTimestamp(),
      xp_debug: {
        source: debugInfo.source || 'manual',
        notes: debugInfo.notes || '',
        lastAdjustment: pointsDelta,
        adjustedAt: new Date().toISOString(),
      },
    },
    { merge: true }
  )

  log.info('User XP adjusted', { uid, newPoints, newLevel: levelInfo.level })

  return {
    newPoints,
    newLevel: levelInfo.level,
    levelInfo,
  }
}
